export function parseUTCDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;

  const raw = String(dateString).trim()
  if (!raw) return null

  if (/^\d+$/.test(raw)) {
    const num = Number(raw)
    if (!Number.isFinite(num)) return null
    const ms = raw.length === 10 ? num * 1000 : num
    const date = new Date(ms)
    if (isNaN(date.getTime())) {
      console.warn('[dateUtils] Invalid epoch date string:', dateString)
      return null
    }
    return date
  }

  const hasTimeZoneInfo =
    /[zZ]$/.test(raw) ||
    /[+-]\d{2}:?\d{2}$/.test(raw) ||
    /[+-]\d{2}$/.test(raw)

  let normalized = raw

  if (!hasTimeZoneInfo) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      normalized = `${raw}T00:00:00Z`
    }
    else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(raw)) {
      normalized = `${raw.replace(' ', 'T')}Z`
    }
    else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(raw)) {
      normalized = `${raw}Z`
    }
  }

  const date = new Date(normalized)

  if (isNaN(date.getTime())) {
    console.warn('[dateUtils] Invalid date string:', dateString)
    return null
  }

  return date
}

export function formatConversationTime(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffInDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert to 12-hour format (0 becomes 12)
    const displayMinutes = minutes.toString().padStart(2, '0');

    return `${displayHours}:${displayMinutes} ${ampm}`;
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  }
}

export function formatDateSeparator(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffInDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }
}

export function isSameDay(date1: string | null | undefined, date2: string | null | undefined): boolean {
  if (!date1 || !date2) return false;

  const d1 = parseUTCDate(date1);
  const d2 = parseUTCDate(date2);

  if (!d1 || !d2) return false;

  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
}

export function formatMessageTime(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  } else {
    return formatConversationTime(dateString);
  }
}

export function isToday(dateString: string | null | undefined): boolean {
  if (!dateString) return false;

  const date = parseUTCDate(dateString);
  if (!date) return false;

  const now = new Date();
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
}

export function isYesterday(dateString: string | null | undefined): boolean {
  if (!dateString) return false;

  const date = parseUTCDate(dateString);
  if (!date) return false;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
}

export function toUTCString(date: Date): string {
  return date.toISOString();
}

export function nowUTC(): string {
  return new Date().toISOString();
}
